import { Box, useTheme } from "@mui/material";
import { PageDefaultSegment } from "@sis/react-library";
import { PageContainer } from "../../../shared/PageContainer"
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';

export const OtherSamplesVitePluginPage = () => {

    const theme = useTheme();
    const highlightStyle: React.CSSProperties = { backgroundColor: 'blue', color: theme.palette.background.paper };

    return (
        <PageContainer heading='Other Samples - Vite Plugin' icon={<MiscellaneousServicesIcon />}>
            <PageDefaultSegment>
                <h3>Used for build once, deploy many react projects</h3>
                This library has a plugin to be used for build once, deploy many react projects.<br /><br />
                The principal behind this is to not use <u>.env</u> files in your project,
                as these envrionment variables get built into the build scripts.<br /><br />
                Instead you need to seperate the environment variables into a seperate file,
                that can be referenced seperately by the html index page and not be built into the build scripts.<br />
                This vite plugin helps with the referencing of those scripts in the html page.<br />
                In order to acheive this you must do the following in your react project:
                <ul>
                    <li>
                        <b>index.html</b><br />
                        Add a {"<configscripts />"} placeholder at the end of the {"<head>"} section.
                        <Box sx={{ background: theme.palette.action.selected }}>
                            <code>
                                &nbsp;&nbsp;{"<head>"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"..."}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"<configscripts />"}<br />
                                &nbsp;&nbsp;{"</head>"}<br />
                            </code>
                        </Box>
                    </li>
                    <li style={{ paddingTop: '30px' }}>
                        <b>/public/config.js</b><br />
                        Add a <u>config.js</u> file into the <u>public</u> folder that will contain your local environment variables.
                        <Box sx={{ background: theme.palette.action.selected }}>
                            <code>
                                &nbsp;&nbsp;{"const config = {"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"environment: \"local\","}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"clientId: \"f24a06fb-8264-4e74-9ffd-0e397e61a764\","}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"tenantId: \"629ba906-d77e-4dc6-acf3-d71375b281ea\""}<br />
                                &nbsp;&nbsp;{"}"}<br />
                            </code>
                        </Box>
                    </li>
                    <li style={{ paddingTop: '30px' }}>
                        <b>vite.config.ts</b><br />
                        Import the vite plugin <u>htmlConfigScripts</u> from SIS Library.<br />
                        When used, it requires the package file as a parameter to get the version.<br />
                        It uses this version to append a query string to the config.js file when called to prevent browser caching between version changes.
                        <Box sx={{ background: theme.palette.action.selected }}>
                            <code>
                                &nbsp;&nbsp;{"import { defineConfig } from 'vitest/config';"}<br />
                                &nbsp;&nbsp;{"import react from '@vitejs/plugin-react';"}<br />
                                &nbsp;&nbsp;<span style={highlightStyle}>{"import packageJson from './package.json' with { type: 'json' };"}</span><br />
                                &nbsp;&nbsp;<span style={highlightStyle}>{"import { htmlConfigScripts } from '@sis/react-library';"}</span><br />
                                <br />
                                &nbsp;&nbsp;{"export default defineConfig({"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;plugins: [react(), <span style={highlightStyle}>htmlConfigScripts(packageJson)</span>],<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"..."}<br />
                                &nbsp;&nbsp;{"})"}<br />
                            </code>
                        </Box>
                        As a side not if you want to minify the html page when built you can use the following package<br />
                        <code>"vite-plugin-html-minifier"</code><br />
                        install it using npm or yarn and then use it as follows
                        <Box sx={{ background: theme.palette.action.selected }}>
                            <code>
                                &nbsp;&nbsp;{"..."}<br />
                                &nbsp;&nbsp;<span style={highlightStyle}>{"import htmlMinifier from 'vite-plugin-html-minifier';"}</span><br />
                                <br />
                                &nbsp;&nbsp;{"export default defineConfig({"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;plugins: [react(), htmlConfigScripts(packageJson), <span style={highlightStyle}>htmlMinifier()</span>],<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"..."}<br />
                                &nbsp;&nbsp;{"})"}<br />
                            </code>
                        </Box>
                    </li>
                    <li style={{ paddingTop: '30px' }}>
                        <b>/src/config.ts</b><br />
                        Add the following file with the below contents, which will typesafe the environment variables to globalThis.config.
                        <Box sx={{ background: theme.palette.action.selected }}>
                            <code>
                                &nbsp;&nbsp;{"type IConfig = {"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"environment: string,"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"clientId: string,"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"tenantId: string"}<br />
                                &nbsp;&nbsp;{"}"}<br />
                                <br />
                                &nbsp;&nbsp;{"declare global {"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"var config: IConfig;"}<br />
                                &nbsp;&nbsp;{"}"}<br />
                            </code>
                        </Box>
                    </li>
                    <li style={{ paddingTop: '30px' }}>
                        <b>/src/setupTests.ts</b><br />
                        In your setupTests.ts file (location could differ above), set the config variable for unit tests.
                        <Box sx={{ background: theme.palette.action.selected }}>
                            <code>
                                &nbsp;&nbsp;{"globalThis.config = {"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"environment: \"test\","}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"clientId: \"1111\","}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"tenantId: \"2222\","}<br />
                                &nbsp;&nbsp;{"}"}
                            </code>
                        </Box>
                        The setupTests.ts file is referenced in the vite.config.ts file.
                        <Box sx={{ background: theme.palette.action.selected }}>
                            <code>
                                &nbsp;&nbsp;{"export default defineConfig({"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"..."}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"test: {"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"environment: 'jsdom',"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"globals: true,"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"setupFiles: './src/setupTests.ts',"}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"..."}<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"},"}<br />
                                &nbsp;&nbsp;{"})"}
                            </code>
                        </Box>
                    </li>
                    <li style={{ paddingTop: '30px' }}>
                        <b>How to reference the config in the code</b><br />
                        The environment config is used by accessing globalThis.config (and is typesafe), here is an example.
                        <Box sx={{ background: theme.palette.action.selected }}>
                            <code>
                                &nbsp;&nbsp;{"const env = globalThis.config.environment;"}
                            </code>
                        </Box>
                    </li>
                    <li style={{ paddingTop: '30px' }}>
                        <b>Deploying</b><br />
                        The SIS pipeline will generate a <u>config.js</u> file for the environment it is deploying to.
                    </li>                    
                </ul>
            </PageDefaultSegment>
        </PageContainer>
    )
}
